'use client'

import { useEffect, useRef, useState } from 'react'
import { MartiniWordmark } from '@/components/marketing/MartiniWordmark'
import { poster, Still } from '../components/ProductionShared'

// One loop, read left to right: the tools a studio built send work into Martini, the studio's
// people and agents shape it there together, and the results flow back into the same tools.

const logo = (name: string) => `/marketing/logos-mono/${name}.svg`

const TOOLS = [
  {
    app: 'Character bible',
    builtWith: 'Lovable',
    mark: 'lovable',
    still: 29,
    maker: 'Director',
  },
  { app: 'Script breakdown', builtWith: 'Claude Code', mark: 'claudecode', maker: 'Producer' },
  { app: 'Prop library', builtWith: 'Cursor', mark: 'cursor', still: 30, maker: 'Production designer' },
]

// The people on the project, by role. Colours match their cursors on the canvas.
const TEAM = [
  { role: 'Client', color: '#c4c4c4', still: 22, pos: '78% 30%' },
  { role: 'Director', color: '#f5a623', still: 29, pos: '68% 28%' },
  { role: 'AI artist', color: '#5b9ef5', still: 6, pos: '55% 30%' },
  { role: 'Production designer', color: '#e85d75', still: 30, pos: '40% 35%' },
  { role: 'Costume designer', color: '#c084fc', still: 22, pos: '25% 35%' },
  { role: 'Producer', color: '#34d399', still: 6, pos: '30% 30%' },
  { role: 'Editor', color: '#fb923c', still: 29, pos: '35% 30%' },
]

// What each card does once it's connected, shown under Martini when it's hovered.
const TOOL_NOTES = [
  'Your lead, supporting cast and cameos land in Martini’s library, so every shot keeps the same faces.',
  'Scene 12 becomes six draft shots on the canvas, ready to generate.',
  'The brass key becomes a reference any shot can use.',
]
const TEAM_NOTES: Record<string, string> = {
  Client: 'Watches the cut and leaves comments right on the shots.',
  Director: 'Picks takes and approves the cut, and still plans in the character bible.',
  'AI artist': 'Generates takes with every model, side by side on the canvas.',
  'Production designer': 'Pulls props and locations into shots as references.',
  'Costume designer': 'Keeps wardrobe consistent across every shot.',
  Producer: 'Sees spend by shot and person, and still breaks down scripts in your tool.',
  Editor: 'Cuts on the same timeline, then exports XML to Premiere or Resolve.',
}
type Focus = { side: 'tool' | 'team'; i: number } | null

const ROWS_3 = { rows: [16.67, 50, 83.33], ends: [36, 50, 64] }
// Evenly spaced rows for a column of n tiles, fanning into the middle third of Martini.
// `band` is the share of the column's height the tiles occupy, centred (1 = the full height).
const rowsFor = (n: number, band = 1) => ({
  rows: Array.from({ length: n }, (_, i) => 50 + ((i + 0.5) / n - 0.5) * 100 * band),
  ends: Array.from({ length: n }, (_, i) => 30 + (40 * i) / Math.max(1, n - 1)),
})
// The team sits in a tighter 340px band inside the 470px column.
const ROWS_TEAM = rowsFor(TEAM.length, 340 / 470)
const colorOf = (role: string) => TEAM.find(m => m.role === role)!.color
const CYCLE = 3.6

const CSS = `
@keyframes loop-in { from { stroke-dashoffset: 100 } to { stroke-dashoffset: 0 } }
@keyframes loop-back { from { stroke-dashoffset: 0 } to { stroke-dashoffset: 100 } }
.loop-in { animation: loop-in ${CYCLE}s linear infinite }
.loop-back { animation: loop-back ${CYCLE}s linear infinite }
@keyframes loop-cursor-a { 0%,100% { transform: translate(0,0) } 25% { transform: translate(-60px,18px) } 50% { transform: translate(-30px,52px) } 75% { transform: translate(18px,26px) } }
@keyframes loop-cursor-b { 0%,100% { transform: translate(0,0) } 30% { transform: translate(70px,-20px) } 55% { transform: translate(110px,24px) } 80% { transform: translate(40px,10px) } }
@keyframes loop-cursor-c { 0%,100% { transform: translate(0,0) } 35% { transform: translate(-80px,-30px) } 65% { transform: translate(-20px,-50px) } }
.loop-cursor { animation: loop-cursor-a 4.2s ease-in-out infinite }
.loop-cursor-2 { animation-name: loop-cursor-b; animation-duration: 5.1s }
.loop-cursor-3 { animation-name: loop-cursor-c; animation-duration: 3.7s }
@keyframes loop-playhead { from { left: 4% } to { left: 96% } }
.loop-playhead { animation: loop-playhead 7s linear infinite }
@media (prefers-reduced-motion: reduce) { .loop-in, .loop-back { display: none } .loop-cursor, .loop-playhead { animation: none } .loop-playhead { left: 40% } }
`

// The wires for one side: a pulse running into Martini and a fainter one running back out.
function Wires({
  flip = false,
  layout = ROWS_3,
  active = null,
}: {
  flip?: boolean
  layout?: typeof ROWS_3
  active?: number | null
}) {
  const { rows: ROWS, ends: ENDS } = layout
  return (
    <div className="relative h-full">
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className={`absolute inset-0 h-full w-full ${flip ? '-scale-x-100' : ''}`}
        aria-hidden
      >
        {ROWS.map((y, i) => {
          const d = `M0,${y} C55,${y} 45,${ENDS[i]} 100,${ENDS[i]}`
          const on = active === i
          return (
            <g
              key={y}
              fill="none"
              opacity={active === null || on ? 1 : 0.15}
              className="transition-opacity duration-200"
            >
              <path
                d={d}
                stroke={on ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.16)'}
                strokeWidth={on ? 1.6 : 1}
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={d}
                pathLength={100}
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="8 92"
                vectorEffect="non-scaling-stroke"
                className={flip ? 'loop-back' : 'loop-in'}
                style={{ animationDelay: `${-i * 1.1}s` }}
              />
              <path
                d={d}
                pathLength={100}
                stroke="rgba(255,255,255,0.45)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="5 95"
                vectorEffect="non-scaling-stroke"
                className={flip ? 'loop-in' : 'loop-back'}
                style={{ animationDelay: `${-i * 1.1 - CYCLE / 2}s` }}
              />
            </g>
          )
        })}
      </svg>
    </div>
  )
}

// Each tool is drawn as its own little app, with a peek at what's inside, so it reads as
// software the studio made and is proud of, not a line item.
function ToolTile({ t }: { t: (typeof TOOLS)[number] }) {
  return (
    <div className="overflow-hidden rounded-xl border border-white/[0.14] bg-[#161616] shadow-[0_10px_30px_-15px_rgba(0,0,0,0.8)]">
      <div className="flex items-center gap-1.5 border-b border-white/[0.07] bg-white/[0.03] px-2.5 py-1.5">
        {[0, 1, 2].map(i => (
          <span key={i} className="h-1.5 w-1.5 rounded-full bg-white/20" />
        ))}
        <span className="ms-1 truncate text-[13px] font-medium text-white/90">{t.app}</span>
      </div>
      <div className="p-2">
        <ToolPreview kind={t.app} />
      </div>
      <div className="flex items-center gap-1.5 border-t border-white/[0.07] px-2.5 py-1.5 text-[12px] text-white/50">
        <span>Built with</span>
        {/* eslint-disable-next-line @next/next/no-img-element -- static brand mark */}
        <img src={logo(t.mark)} alt="" className="h-3 w-3 shrink-0 object-contain" />
        <span className="shrink-0 font-medium text-white/80">{t.builtWith}</span>
      </div>
    </div>
  )
}

function ToolPreview({ kind }: { kind: string }) {
  if (kind === 'Character bible') {
    return (
      <div className="grid grid-cols-3 gap-1.5">
        {[
          { n: 'Lead', p: 29, pos: '60% 30%' },
          { n: 'Supporting', p: 22, pos: '20% 40%' },
          { n: 'Cameo', p: 6, pos: '50% 35%' },
        ].map(c => (
          <span key={c.n} className="block">
            <Still src={poster(c.p)} pos={c.pos} className="h-10 w-full rounded-md" />
            <span className="mt-1 block text-center text-[11px] text-white/55">{c.n}</span>
          </span>
        ))}
      </div>
    )
  }
  if (kind === 'Script breakdown') {
    return (
      <div className="space-y-1.5 font-mono text-[9.5px] leading-none">
        <div className="flex items-center gap-1.5">
          <span className="rounded bg-white/90 px-1 py-0.5 font-semibold text-black">SC 12</span>
          <span className="text-white/75">INT. KITCHEN – NIGHT</span>
        </div>
        {[
          ['Lead', '72%'],
          ['Supporting', '54%'],
          ['Brass key', '38%'],
        ].map(([who, w]) => (
          <div key={who} className="flex items-center gap-1.5">
            <span className="w-[62px] shrink-0 truncate text-white/45">{who}</span>
            <span className="h-1.5 rounded-full bg-white/15" style={{ width: w }} />
          </div>
        ))}
      </div>
    )
  }
  return (
    <div className="grid grid-cols-4 gap-1">
      {[30, 22, 29, 6].map((p, i) => (
        <Still key={i} src={poster(p)} pos={`${20 + i * 20}% 60%`} className="h-10 w-full rounded-md" />
      ))}
    </div>
  )
}

function TeamTile({ m }: { m: (typeof TEAM)[number] }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-white/[0.12] bg-[#161616] px-2 py-1.5">
      <span
        className="relative shrink-0 rounded-full"
        style={{ boxShadow: `0 0 0 1.5px #161616, 0 0 0 3px ${m.color}` }}
      >
        <Still src={poster(m.still)} pos={m.pos} className="block h-6 w-6 rounded-full" />
      </span>
      <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-white">{m.role}</span>
      <svg width="10" height="10" viewBox="0 0 12 12" style={{ fill: m.color }} aria-hidden>
        <path d="M0 0l11 4.5-4.5 1.5L5 11z" />
      </svg>
    </div>
  )
}

// A small replica of the timeline: two video tracks of shots, a sound track, and a playhead
// that sweeps across. Clip widths are seconds, so the tracks line up like a real cut.
const V2 = [{ w: 2, p: 30 }, { w: 3 }, { w: 2, p: 29 }, { w: 3 }]
const V1 = [
  { w: 3, p: 22 },
  { w: 2, p: 6 },
  { w: 3, p: 29 },
  { w: 2, p: 22 },
]
const WAVE = Array.from({ length: 56 }, (_, i) => 0.25 + 0.75 * Math.abs(Math.sin(i * 1.7) * Math.cos(i * 0.45)))

function MiniTimeline() {
  return (
    <div className="mt-1.5 rounded-xl bg-[#141414] p-1.5" aria-hidden>
      <div className="relative space-y-1">
        {[V2, V1].map((track, t) => (
          <div key={t} className="flex h-6 gap-0.5">
            {track.map((c, i) =>
              c.p === undefined ? (
                <span key={i} style={{ flex: c.w }} />
              ) : (
                <span key={i} className="overflow-hidden rounded-[3px] ring-1 ring-white/15" style={{ flex: c.w }}>
                  <Still src={poster(c.p)} className="h-full w-full" />
                </span>
              ),
            )}
          </div>
        ))}
        <div className="flex h-5 items-center gap-px rounded-[3px] bg-[#5b9ef5]/15 px-1">
          {WAVE.map((h, i) => (
            <span
              key={i}
              className="flex-1 rounded-full bg-[#5b9ef5]/70"
              style={{ height: `${Math.round(h * 100)}%` }}
            />
          ))}
        </div>
        <span className="loop-playhead absolute -bottom-0.5 -top-0.5 w-px bg-white">
          <span className="absolute -left-[3px] -top-0.5 h-1.5 w-[7px] rounded-sm bg-white" />
        </span>
      </div>
    </div>
  )
}

// Martini in the middle, in the editor's own dark colours: the canvas, people and an agent on it
// at once, and the timeline under it.
function MartiniCanvas() {
  return (
    <div className="relative w-full rounded-2xl border border-white/20 bg-[#1c1c1c] p-3 text-white shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_30px_90px_-30px_rgba(255,255,255,0.18)]">
      <div className="flex items-center justify-between px-1 pb-2.5">
        <MartiniWordmark className="h-5 w-auto fill-white" />
        <span className="flex -space-x-1.5" aria-hidden>
          {TEAM.map(m => (
            <span
              key={m.role}
              className="h-4 w-4 rounded-full border-2 border-[#1c1c1c]"
              style={{ background: m.color }}
            />
          ))}
        </span>
      </div>
      <div className="relative grid grid-cols-3 gap-1.5 rounded-xl bg-[#141414] p-1.5">
        {[29, 22, 6, 30, 22, 6].map((p, i) => (
          <Still
            key={i}
            src={poster(p)}
            pos={i > 2 ? '70% 50%' : '30% 50%'}
            className="aspect-video w-full rounded-md"
          />
        ))}
        {[
          { name: 'Director', color: colorOf('Director'), top: '18%', left: '62%', cls: 'loop-cursor' },
          { name: 'Claude Code', color: '#8b8bf5', top: '58%', left: '10%', cls: 'loop-cursor loop-cursor-2' },
          { name: 'AI artist', color: colorOf('AI artist'), top: '70%', left: '72%', cls: 'loop-cursor loop-cursor-3' },
        ].map(({ name, color, top, left, cls }) => (
          <span key={name} className={`${cls} absolute flex items-start gap-0.5`} style={{ top, left }} aria-hidden>
            <svg width="11" height="11" viewBox="0 0 12 12" style={{ fill: color }}>
              <path d="M0 0l11 4.5-4.5 1.5L5 11z" />
            </svg>
            <span
              className="mt-2 whitespace-nowrap rounded px-1 py-px text-[9px] font-medium text-black"
              style={{ background: color }}
            >
              {name}
            </span>
          </span>
        ))}
      </div>
      <MiniTimeline />
    </div>
  )
}

const head = 'text-[18px] font-semibold tracking-[-0.01em] text-white'
const sub = 'mt-1 whitespace-nowrap text-[14px] text-white/60'

// The director and the producer still live in their own tools: a faint dotted arc runs from each
// straight back to the tool they use, around Martini, so the loop reads "both", not "instead".
// Team index → tool index.
const DIRECT = [
  [1, 0], // Director → Character bible, over the top of Martini
  [5, 1], // Producer → Script breakdown, underneath
]

function useDirectArcs(grid: React.RefObject<HTMLDivElement | null>) {
  const [arcs, setArcs] = useState<{ d: string; key: string; team: number; tool: number }[]>([])
  const [label, setLabel] = useState<{ x: number; y: number } | null>(null)
  useEffect(() => {
    const el = grid.current
    if (!el) return
    const measure = () => {
      const box = el.getBoundingClientRect()
      if (!box.width) return
      const at = (sel: string) => el.querySelector(sel)?.getBoundingClientRect()
      const canvas = at('[data-martini] > div')
      const floor = canvas ? canvas.bottom - box.top + 30 : box.height - 20
      const ceiling = canvas ? canvas.top - box.top - 34 : 20
      const next = DIRECT.flatMap(([team, tool]) => {
        const a = at(`[data-team="${team}"]`)
        const b = at(`[data-tool="${tool}"]`)
        if (!a || !b) return []
        const x1 = a.left - box.left
        const y1 = a.top - box.top + a.height / 2
        const x2 = b.right - box.left
        const y2 = b.top - box.top + b.height / 2
        const y = tool === 0 ? ceiling : floor
        return [
          {
            team,
            tool,
            key: `${team}-${tool}`,
            d: `M${x1} ${y1} C ${x1 - 90} ${y1}, ${x1 - 60} ${y}, ${(x1 + x2) / 2} ${y} S ${x2 + 90} ${y2}, ${x2} ${y2}`,
          },
        ]
      })
      setArcs(next)
      setLabel({ x: box.width / 2, y: floor + 36 })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [grid])
  return { arcs, label }
}

export function StudioLoop() {
  const grid = useRef<HTMLDivElement>(null)
  const { arcs, label } = useDirectArcs(grid)
  const [focus, setFocus] = useState<Focus>(null)
  const note = !focus
    ? 'Hover any tool or person to see what they do here.'
    : focus.side === 'tool'
      ? TOOL_NOTES[focus.i]
      : ''
  // A tool and the person who made it light up together.
  const linkedTeam = focus?.side === 'tool' ? TEAM.findIndex(m => m.role === TOOLS[focus.i].maker) : -1
  const linkedTool = focus?.side === 'team' ? TOOLS.findIndex(t => t.maker === TEAM[focus.i].role) : -1
  const lit = (side: 'tool' | 'team', i: number) =>
    !focus ||
    (focus.side === side && focus.i === i) ||
    (side === 'team' && i === linkedTeam) ||
    (side === 'tool' && i === linkedTool)
  const dimmed = (side: 'tool' | 'team', i: number) => (lit(side, i) ? 'opacity-100' : 'opacity-40')
  return (
    <div className="@container p-5 md:p-8">
      <style>{CSS}</style>
      <div
        ref={grid}
        className="relative hidden grid-cols-[250px_minmax(0,1fr)_minmax(250px,300px)_minmax(0,1fr)_250px] gap-y-4 pb-16 @[900px]:grid"
      >
        <svg className="pointer-events-none absolute inset-0 h-full w-full overflow-visible" aria-hidden>
          {arcs.map(a => {
            const on = (focus?.side === 'tool' && focus.i === a.tool) || (focus?.side === 'team' && focus.i === a.team)
            return (
              <path
                key={a.key}
                d={a.d}
                fill="none"
                stroke={on ? 'rgba(255,255,255,0.85)' : focus ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.28)'}
                className="transition-[stroke] duration-200"
                strokeWidth={on ? 1.6 : 1.2}
                strokeDasharray="2 5"
                strokeLinecap="round"
              />
            )
          })}
        </svg>
        {label && (
          <span
            className="pointer-events-none absolute -translate-x-1/2 whitespace-nowrap text-[14px] text-white/55"
            style={{ left: label.x, top: label.y }}
          >
            Your team keeps using your tools, too
          </span>
        )}
        <div>
          <p className={head}>Your tools</p>
          <p className={sub}>Built in-house for scripts, cast and props</p>
        </div>
        <div className="col-start-3 text-center">
          <p className={head}>Martini</p>
          <p className={sub}>Where shots are generated, reviewed and cut</p>
        </div>
        <div className="col-start-5">
          <p className={head}>Your team</p>
          <p className={sub}>And your agents, on one project</p>
        </div>

        <div className="row-start-2 grid h-[470px] grid-rows-3 items-center">
          {TOOLS.map((t, i) => (
            <div
              key={t.app}
              data-tool={i}
              onMouseEnter={() => setFocus({ side: 'tool', i })}
              onMouseLeave={() => setFocus(null)}
              className={`relative cursor-pointer transition-[opacity,transform] duration-200 hover:-translate-y-0.5 ${dimmed('tool', i)}`}
            >
              {/* Who made it and with what, popping out to the left while it's hovered. */}
              <span
                className={`pointer-events-none absolute right-full top-1/2 me-3 w-max -translate-y-1/2 whitespace-nowrap rounded-xl border border-white/15 bg-[#161616] px-3 py-2 text-[12px] leading-snug text-white/75 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.8)] transition-all duration-200 ${
                  focus?.side === 'tool' && focus.i === i ? 'translate-x-0 opacity-100' : 'translate-x-2 opacity-0'
                }`}
              >
                <span className="flex items-center gap-1.5 font-medium text-white">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: colorOf(t.maker) }} />
                  {t.maker}
                </span>
                <span className="mt-1 flex items-center gap-1.5 text-white/55">
                  built it with
                  {/* eslint-disable-next-line @next/next/no-img-element -- static brand mark */}
                  <img src={logo(t.mark)} alt="" className="h-3 w-3 object-contain" />
                  <span className="font-medium text-white">{t.builtWith}</span>
                </span>
              </span>
              <ToolTile t={t} />
            </div>
          ))}
        </div>
        <div className="row-start-2">
          <Wires active={focus?.side === 'tool' ? focus.i : focus ? linkedTool : null} />
        </div>
        <div className="row-start-2 flex items-center" data-martini>
          <div className="w-full">
            <MartiniCanvas />
            {/* What the hovered tool or person does here; fixed height so nothing jumps. */}
            <p
              key={note}
              className="animate-in fade-in mt-4 h-[60px] text-balance text-center text-[15px] leading-snug text-white/70 duration-200"
            >
              {note}
            </p>
          </div>
        </div>
        <div className="row-start-2">
          <Wires flip layout={ROWS_TEAM} active={focus?.side === 'team' ? focus.i : focus ? linkedTeam : null} />
        </div>
        <div className="row-start-2 flex h-[470px] items-center">
          <div className="grid h-[340px] w-full grid-rows-7 items-center">
            {TEAM.map((m, i) => (
              <div
                key={m.role}
                data-team={i}
                onMouseEnter={() => setFocus({ side: 'team', i })}
                onMouseLeave={() => setFocus(null)}
                className={`relative cursor-pointer transition-[opacity,transform] duration-200 hover:-translate-x-0.5 ${dimmed('team', i)}`}
              >
                <TeamTile m={m} />
                {/* What they do here, popping out to the right while hovered. */}
                <span
                  className={`pointer-events-none absolute left-full top-1/2 z-10 ms-3 w-[170px] -translate-y-1/2 rounded-xl border border-white/15 bg-[#161616] px-3 py-2 text-[12px] leading-snug text-white/75 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.8)] transition-all duration-200 ${
                    focus?.side === 'team' && focus.i === i ? 'translate-x-0 opacity-100' : '-translate-x-2 opacity-0'
                  }`}
                >
                  {TEAM_NOTES[m.role]}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Narrower: the same loop, top to bottom, kept short. One tool stands in for all of them and
          the team is a row of faces, so the page reaches the starter prompt within a screen. */}
      <div className="grid gap-3 @[900px]:hidden">
        <p className={head}>Your tools</p>
        <ToolTile t={TOOLS[1]} />
        <p className="text-center text-[18px] text-white/40" aria-hidden>
          ↓
        </p>
        <p className={head}>Martini</p>
        <MartiniCanvas />
        <p className="text-center text-[18px] text-white/40" aria-hidden>
          ↓
        </p>
        <p className={head}>Your team, and your agents</p>
        <div className="flex flex-wrap items-center gap-2">
          {TEAM.map(m => (
            <span
              key={m.role}
              title={m.role}
              className="relative shrink-0 rounded-full"
              style={{ boxShadow: `0 0 0 1.5px #141414, 0 0 0 3px ${m.color}` }}
            >
              <Still src={poster(m.still)} pos={m.pos} className="block h-8 w-8 rounded-full" />
            </span>
          ))}
        </div>
        <p className="text-[14px] text-white/60">{TEAM.map(m => m.role).join(', ')}</p>
      </div>
    </div>
  )
}
